"use client";

import { useMemo } from "react";
import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { BaseEntity } from "@/lib/types/table/shared";
import { contactsEntity } from "../entities/contacts";
import { companiesEntity } from "../entities/companies";
import { dealsEntity } from "../entities/deals";
import { ticketsEntity } from "../entities/tickets";
import { tasksEntity } from "../entities/tasks";
import { socialMediaPostsEntity } from "../entities/social-media-posts";
import { emailCampaignsEntity } from "../entities/email-campaigns";

type RegistryConfigEnhancer = (
  config: SimpleTableConfig<BaseEntity>
) => SimpleTableConfig<BaseEntity>;

function callEnhancer<T extends BaseEntity>(
  enhancer: RegistryConfigEnhancer,
  config: SimpleTableConfig<T>
): SimpleTableConfig<T> {
  return enhancer(config as unknown as SimpleTableConfig<BaseEntity>) as unknown as SimpleTableConfig<T>;
}

/**
 * Applies the entity registry's `useConfigEnhancer` for dynamic field options.
 * All enhancer hooks are invoked unconditionally to satisfy Rules of Hooks.
 */
export function useRegistryConfigEnhancer<T extends BaseEntity>(
  entityName: string,
  config: SimpleTableConfig<T>
): SimpleTableConfig<T> {
  const contactsEnhanced = callEnhancer(
    contactsEntity.useConfigEnhancer as unknown as RegistryConfigEnhancer,
    config
  );
  const companiesEnhanced = callEnhancer(
    companiesEntity.useConfigEnhancer as unknown as RegistryConfigEnhancer,
    config
  );
  const dealsEnhanced = callEnhancer(
    dealsEntity.useConfigEnhancer as unknown as RegistryConfigEnhancer,
    config
  );
  const ticketsEnhanced = callEnhancer(
    ticketsEntity.useConfigEnhancer as unknown as RegistryConfigEnhancer,
    config
  );
  const tasksEnhanced = callEnhancer(
    tasksEntity.useConfigEnhancer as unknown as RegistryConfigEnhancer,
    config
  );
  const socialPostsEnhanced = callEnhancer(
    socialMediaPostsEntity.useConfigEnhancer as unknown as RegistryConfigEnhancer,
    config
  );
  const emailCampaignsEnhanced = callEnhancer(
    emailCampaignsEntity.useConfigEnhancer as unknown as RegistryConfigEnhancer,
    config
  );

  return useMemo(() => {
    switch (entityName) {
      case "contacts":
        return contactsEnhanced as SimpleTableConfig<T>;
      case "companies":
        return companiesEnhanced as SimpleTableConfig<T>;
      case "deals":
        return dealsEnhanced as SimpleTableConfig<T>;
      case "tickets":
        return ticketsEnhanced as SimpleTableConfig<T>;
      case "tasks":
        return tasksEnhanced as SimpleTableConfig<T>;
      case "social_media_posts":
        return socialPostsEnhanced as SimpleTableConfig<T>;
      case "email_campaigns":
        return emailCampaignsEnhanced as SimpleTableConfig<T>;
      default:
        return config;
    }
  }, [
    entityName,
    config,
    contactsEnhanced,
    companiesEnhanced,
    dealsEnhanced,
    ticketsEnhanced,
    tasksEnhanced,
    socialPostsEnhanced,
    emailCampaignsEnhanced,
  ]);
}
